import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Fragment, useMemo } from 'react'
import { Modal } from '@opallabs/lapidary'
import { AddToBoardFooter } from './Footer'
import { useQueryableAddBoardModal } from './forBackboneConsumers/QueryableAddMomentToBoardContext'
import { AddMomentToBoardModalContent } from './ModalContent'
import * as styles from './styles'
import { AddMomentToBoardModalProps } from './types'
import { useAddBoardState } from './useAddToBoardState'

export { useOnAddToBoard } from './queries/useOnAddToBoard'
export * from './AddToBoardToasts'
export type {
  OnCloseAddToBoardModal,
  OnCloseAddToBoardModalChanges,
  AddToBoardSuccessCallback,
  OnAddToBoardArgs,
  AddToBoardResponseData,
  AddToBoardMutationParams,
} from './types'

export const AddMomentToBoardModal: React.FC<AddMomentToBoardModalProps> = ({
  isVisible,
  limitOptions = false,
  momentUuids,
  onAddToBoard,
  onCloseModal,
  relatedResources,
  unscheduleByDefault = false,
}) => {
  const backboneCallback = useQueryableAddBoardModal()
  const onAddBoardCallback = backboneCallback ?? onAddToBoard

  const [validIds, invalidIdCount] = useMemo(() => {
    const validIds = momentUuids.filter((id) => id !== null)
    const invalidIds = momentUuids.filter((id) => id === null)
    return [validIds, invalidIds.length]
  }, [momentUuids])

  const {
    boardOptions,
    boardSelectionChanged,
    canUnschedule,
    hasError,
    initialSelection,
    isLoading,
    moments,
    selectedBoards,
    setSelectedBoards,
    setUnschedule,
    showEmptyBoardErrorAfterInteraction,
    unschedule,
  } = useAddBoardState({ momentUuids: validIds, unscheduleByDefault })

  return (
    <Modal.Root
      open={isVisible}
      onOpenChange={(open) => !open && onCloseModal()}
    >
      <Modal.Content
        key={validIds.map((uuid) => uuid.slice(0, 4)).join('')}
        onEscapeKeyDown={() => onCloseModal()}
        css={styles.modalContent}
      >
        {isVisible && (
          <Fragment>
            <Modal.Title>Add to Board</Modal.Title>
            <VisuallyHidden asChild>
              <Modal.Description>
                Select one or more boards to add this moment to.
              </Modal.Description>
            </VisuallyHidden>
            <Modal.Close />
            <Modal.Body css={styles.modalBody}>
              <AddMomentToBoardModalContent
                boardOptions={boardOptions}
                canUnschedule={canUnschedule}
                hasError={hasError}
                limitOptions={limitOptions}
                initialSelection={initialSelection}
                invalidMomentCount={invalidIdCount}
                isLoading={isLoading}
                momentCount={momentUuids.length}
                relatedResources={relatedResources}
                selectedBoards={selectedBoards}
                setSelectedBoards={setSelectedBoards}
                setUnschedule={setUnschedule}
                showEmptyBoardErrorAfterInteraction={
                  showEmptyBoardErrorAfterInteraction
                }
                unschedule={unschedule}
              />
            </Modal.Body>
          </Fragment>
        )}
        <AddToBoardFooter
          boardSelectionChanged={boardSelectionChanged}
          hasError={hasError}
          initialSelection={initialSelection}
          isLoading={isLoading}
          invalidIdCount={invalidIdCount}
          moments={moments}
          momentCount={momentUuids.length}
          onAddToBoard={onAddBoardCallback}
          onCloseModal={onCloseModal}
          selectedBoards={selectedBoards}
          unschedule={unschedule}
        />
      </Modal.Content>
    </Modal.Root>
  )
}
